import { Icon } from "@iconify/react/dist/iconify.js";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Modal, Nav, Tab } from "react-bootstrap";
import { handleError, handleSuccess } from "../CommonJquery/CommonJquery";
import MasterLayout from "../masterLayout/MasterLayout";
import {
  create_twitter_tweet,
  delete_twitter_tweet,
  get_twitter_account,
  get_twitter_auth_url,
  get_twitter_insights,
  get_twitter_mentions,
  get_twitter_replies,
  get_twitter_tweets,
  react_twitter_tweet,
  save_twitter_account,
  search_twitter_tweets,
  server_post_data,
  twitter_oauth_callback,
  upload_twitter_media,
} from "../ServiceConnection/serviceconnection";
import Loader from "./Loader";
import "../components/CSS/gmb-premium.css";
import "../components/CSS/facebook-premium.css";
import "../components/CSS/twitter-premium.css";

/* ---------------------------------------------------------------------------
   Static config
--------------------------------------------------------------------------- */

const MAX_CHARS = 280;

const SOCIAL_PROFILES = [
  { key: "google", label: "Google Business", icon: "mdi:google", ready: false },
  { key: "facebook", label: "Facebook", icon: "mdi:facebook", ready: false },
  { key: "instagram", label: "Instagram", icon: "mdi:instagram", ready: false },
  { key: "twitter", label: "Twitter", icon: "ri:twitter-x-fill", ready: true },
  { key: "linkedin", label: "LinkedIn", icon: "mdi:linkedin", ready: false },
  { key: "pinterest", label: "Pinterest", icon: "mdi:pinterest", ready: false },
];

const CHANNEL_TABS = [
  { key: "twitter", label: "Twitter", ready: true },
  { key: "facebook", label: "Facebook", ready: false },
  { key: "instagram", label: "Instagram", ready: false },
  { key: "linkedin", label: "LinkedIn", ready: false },
  { key: "google", label: "Google", ready: false },
  { key: "pinterest", label: "Pinterest", ready: false },
];

const POST_TYPES = [
  { key: "TWEET", label: "Single Tweet", icon: "mdi:message-outline" },
  { key: "THREAD", label: "Thread", icon: "mdi:format-list-numbered" },
  { key: "POLL", label: "Poll", icon: "mdi:poll" },
];

const POLL_DURATIONS = [
  { key: 60, label: "1 hour" },
  { key: 1440, label: "1 day" },
  { key: 4320, label: "3 days" },
  { key: 10080, label: "7 days" },
];

const REPLY_SETTINGS = [
  { key: "everyone", label: "Everyone" },
  { key: "following", label: "People you follow" },
  { key: "mentionedUsers", label: "Only mentioned users" },
];

const TIPS = [
  {
    title: "One Idea Per Tweet",
    body: "The 280-character limit rewards a single sharp point. Split anything longer into a thread instead of compressing it.",
  },
  {
    title: "Links Cost 23 Characters",
    body: "Every URL counts as 23 characters no matter its real length, so shorteners save you nothing here.",
  },
  {
    title: "Reply To Your Own Replies",
    body: "Staying active in the replies under your tweet keeps it circulating far longer than posting and walking away.",
  },
];

const emptyTweet = {
  post_type: "TWEET",
  text: "",
  poll_question: "",
  poll_duration: 1440,
  reply_settings: "everyone",
};

/* ---------------------------------------------------------------------------
   Character counter ring
--------------------------------------------------------------------------- */

function CharRing({ count, max = MAX_CHARS }) {
  const radius = 10;
  const circumference = 2 * Math.PI * radius;
  const ratio = Math.min(count / max, 1);
  const offset = circumference * (1 - ratio);

  const remaining = max - count;
  const level = remaining < 0 ? "over" : remaining <= 20 ? "warn" : "";

  return (
    <div className="tw-counter">
      <svg className={`tw-ring ${level}`} viewBox="0 0 26 26">
        <circle className="tw-ring-track" cx="13" cy="13" r={radius} />
        <circle
          className="tw-ring-fill"
          cx="13"
          cy="13"
          r={radius}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
        />
      </svg>
      <span className={`tw-counter-value ${level}`}>{remaining} left</span>
    </div>
  );
}

/* ------------------------------------------------------------------------ */

function TwitterPanel() {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("account");

  // account
  const [account, setAccount] = useState(null);
  const pendingAuth = useRef(null);

  // tweets
  const [tweets, setTweets] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(null);

  // composer
  const [showComposer, setShowComposer] = useState(false);
  const [form, setForm] = useState(emptyTweet);
  const [media, setMedia] = useState([]); // [{ mediaId, kind, preview }]
  const [threadItems, setThreadItems] = useState([
    { text: "", media: [] },
    { text: "", media: [] },
  ]);
  const [pollOptions, setPollOptions] = useState(["", ""]);
  const [channel, setChannel] = useState("twitter");

  // replies
  const [showReplies, setShowReplies] = useState(false);
  const [replyTweet, setReplyTweet] = useState(null);
  const [replies, setReplies] = useState([]);
  const [replyBox, setReplyBox] = useState({});

  // insights
  const [insights, setInsights] = useState(null);

  // listening
  const [mentions, setMentions] = useState([]);
  const [query, setQuery] = useState("");
  const [minLikes, setMinLikes] = useState("");
  const [searchResults, setSearchResults] = useState(null);

  const clickRef = useRef(0);

  /* ------------------------- common caller ------------------------- */
  const call = useCallback(async (url, fields = {}, files = {}) => {
    const fd = new FormData();
    Object.entries(fields).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") fd.append(k, v);
    });
    Object.entries(files).forEach(([k, f]) => f && fd.append(k, f));

    try {
      const res = await server_post_data(url, fd);
      const parts = res.message.split("~@~");
      if (parseInt(parts[0]) === 1) {
        handleError(parts[1]);
        return null;
      }
      const parsed = JSON.parse(parts[1]);
      if (parsed.error) {
        handleError(parsed.message || "X API error");
        return null;
      }
      return parsed;
    } catch (e) {
      handleError("network");
      return null;
    }
  }, []);

  /* ------------------------- loaders ------------------------- */
  const loadAccount = useCallback(async () => {
    setLoading(true);
    const d = await call(get_twitter_account);
    if (d?.account) setAccount(d.account);
    setLoading(false);
  }, [call]);

  const loadTweets = useCallback(async () => {
    setLoading(true);
    const d = await call(get_twitter_tweets, { limit: 50 });
    setTweets(d?.tweets || []);
    setLoading(false);
  }, [call]);

  const loadInsights = useCallback(async () => {
    setLoading(true);
    const d = await call(get_twitter_insights, { limit: 50 });
    setInsights(d || null);
    setLoading(false);
  }, [call]);

  const loadMentions = useCallback(async () => {
    setLoading(true);
    const d = await call(get_twitter_mentions, { limit: 40 });
    setMentions(d?.mentions || []);
    setLoading(false);
  }, [call]);

  useEffect(() => {
    loadAccount();
  }, [loadAccount]);

  useEffect(() => {
    if (activeTab === "tweets") loadTweets();
    if (activeTab === "insights") loadInsights();
    if (activeTab === "mentions") loadMentions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  /* ------------------------- OAuth popup flow ------------------------- */
  const exchangeCode = useCallback(
    async (code, state) => {
      setLoading(true);
      const d = await call(twitter_oauth_callback, { code, state });
      setLoading(false);
      if (!d?.account) return;

      pendingAuth.current = d;

      setLoading(true);
      const saved = await call(save_twitter_account, {
        user_id: d.account.userId,
        username: d.account.username,
        access_token: d.accessToken,
        refresh_token: d.refreshToken,
        expires_in: d.expiresIn,
      });
      setLoading(false);

      if (saved) {
        handleSuccess(`Connected as @${d.account.username}`);
        loadAccount();
      }
    },
    [call, loadAccount],
  );

  // X has no JS SDK — the popup posts the auth code back to us here.
  useEffect(() => {
    const onMessage = (event) => {
      if (event.origin !== window.location.origin) return;
      const data = event.data || {};
      if (data.source !== "twitter-oauth") return;

      if (data.error) {
        handleError(data.error);
        return;
      }
      if (data.code) exchangeCode(data.code, data.state);
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [exchangeCode]);

  const handleConnect = async () => {
    const d = await call(get_twitter_auth_url);
    if (!d?.authUrl) return;

    const w = 600;
    const h = 720;
    const left = window.screenX + (window.outerWidth - w) / 2;
    const top = window.screenY + (window.outerHeight - h) / 2;

    const popup = window.open(
      d.authUrl,
      "twitter-oauth",
      `width=${w},height=${h},left=${left},top=${top}`,
    );
    if (!popup) handleError("Please allow popups to connect X");
  };

  /* ------------------------- character counting ------------------------- */
  // X counts every URL as exactly 23 characters, so a naive length check
  // would let over-limit tweets through to a server-side rejection.
  const countChars = useCallback((text) => {
    if (!text) return 0;
    let total = text.length;
    text.split(/\s+/).forEach((w) => {
      if (w.startsWith("http://") || w.startsWith("https://")) {
        total = total - w.length + 23;
      }
    });
    return total;
  }, []);

  const mainCount = useMemo(
    () => countChars(form.text),
    [form.text, countChars],
  );

  /* ------------------------- media ------------------------- */
  const uploadMedia = async (file) => {
    if (!file) return null;
    setLoading(true);
    const d = await call(upload_twitter_media, {}, { media_file: file });
    setLoading(false);
    if (!d) return null;
    return {
      mediaId: d.mediaId,
      kind: d.mediaKind,
      preview: URL.createObjectURL(file),
    };
  };

  const onPickMedia = async (file) => {
    if (media.length >= 4) {
      handleError("A tweet can carry at most 4 media items");
      return;
    }
    const m = await uploadMedia(file);
    if (m) setMedia((s) => [...s, m]);
  };

  const onPickThreadMedia = async (idx, file) => {
    const item = threadItems[idx];
    if ((item.media || []).length >= 4) {
      handleError("Each tweet can carry at most 4 media items");
      return;
    }
    const m = await uploadMedia(file);
    if (!m) return;
    setThreadItems((s) =>
      s.map((it, i) => (i === idx ? { ...it, media: [...it.media, m] } : it)),
    );
  };

  /* ------------------------- publish ------------------------- */
  const resetComposer = () => {
    setForm(emptyTweet);
    setMedia([]);
    setThreadItems([
      { text: "", media: [] },
      { text: "", media: [] },
    ]);
    setPollOptions(["", ""]);
    setChannel("twitter");
  };

  const openComposer = () => {
    resetComposer();
    setShowComposer(true);
  };

  const publish = async () => {
    const t = form.post_type;

    if (t === "THREAD") {
      const filled = threadItems.filter(
        (it) => it.text.trim() || it.media.length,
      );
      if (filled.length < 2) {
        handleError("A thread needs at least 2 tweets");
        return;
      }
      const overIdx = filled.findIndex((it) => countChars(it.text) > MAX_CHARS);
      if (overIdx !== -1) {
        handleError(`Tweet ${overIdx + 1} exceeds ${MAX_CHARS} characters`);
        return;
      }
    } else {
      if (!form.text.trim() && !media.length && t !== "POLL") {
        handleError("Tweet text or media is required");
        return;
      }
      if (mainCount > MAX_CHARS) {
        handleError(`Tweet cannot exceed ${MAX_CHARS} characters`);
        return;
      }
      if (t === "POLL") {
        if (pollOptions.filter((o) => o.trim()).length < 2) {
          handleError("A poll needs at least 2 filled options");
          return;
        }
        if (!form.text.trim()) {
          handleError("Poll tweets still need some text");
          return;
        }
      }
    }

    if (clickRef.current === 1) return;
    clickRef.current = 1;
    setLoading(true);

    let payload;
    if (t === "THREAD") {
      payload = {
        thread: JSON.stringify(
          threadItems
            .filter((it) => it.text.trim() || it.media.length)
            .map((it) => ({
              text: it.text,
              media_ids: it.media.map((m) => m.mediaId),
            })),
        ),
        reply_settings: form.reply_settings,
      };
    } else {
      payload = {
        text: form.text,
        media_ids: JSON.stringify(media.map((m) => m.mediaId)),
        reply_settings: form.reply_settings,
      };
      if (t === "POLL") {
        payload.media_ids = "[]";
        payload.poll_options = JSON.stringify(
          pollOptions.filter((o) => o.trim()),
        );
        payload.poll_duration = form.poll_duration;
      }
    }

    const d = await call(create_twitter_tweet, payload);

    setLoading(false);
    clickRef.current = 0;

    if (d) {
      handleSuccess(
        d.postType === "THREAD"
          ? `Thread posted (${d.totalCount} tweets)`
          : "Tweet posted successfully",
      );
      setShowComposer(false);
      loadTweets();
    }
  };

  const removeTweet = async () => {
    setLoading(true);
    const d = await call(delete_twitter_tweet, { tweet_id: confirmDelete });
    setLoading(false);
    setConfirmDelete(null);
    if (d) {
      handleSuccess("Tweet deleted successfully");
      loadTweets();
    }
  };

  const react = async (tweetId, action) => {
    setLoading(true);
    const d = await call(react_twitter_tweet, {
      tweet_id: tweetId,
      action,
    });
    setLoading(false);
    if (d) handleSuccess(d.message || "Updated successfully");
  };

  /* ------------------------- replies ------------------------- */
  const openReplies = async (t) => {
    setReplyTweet(t);
    setShowReplies(true);
    setLoading(true);
    const d = await call(get_twitter_replies, {
      conversation_id: t.conversationId || t.tweetId,
      limit: 50,
    });
    setReplies(d?.replies || []);
    setLoading(false);
  };

  const sendReply = async (targetId) => {
    const msg = (replyBox[targetId] || "").trim();
    if (!msg) return;
    if (countChars(msg) > MAX_CHARS) {
      handleError(`Reply cannot exceed ${MAX_CHARS} characters`);
      return;
    }
    setLoading(true);
    const d = await call(create_twitter_tweet, {
      text: msg,
      reply_to_id: targetId,
    });
    setLoading(false);
    if (d) {
      handleSuccess("Reply posted successfully");
      setReplyBox((s) => ({ ...s, [targetId]: "" }));
      openReplies(replyTweet);
    }
  };

  /* ------------------------- search ------------------------- */
  const runSearch = async () => {
    if (!query.trim()) {
      handleError("Please enter a search query");
      return;
    }
    setLoading(true);
    const d = await call(search_twitter_tweets, {
      query: query.trim(),
      limit: 40,
      min_likes: minLikes || "",
    });
    setLoading(false);
    if (d) setSearchResults(d);
  };

  /* ------------------------- helpers ------------------------- */
  const initials = (n) => (n || "?").trim().charAt(0).toUpperCase();

  const fmtDate = (d) =>
    d
      ? new Date(d).toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        })
      : "-";

  const compact = (n) => {
    if (n === undefined || n === null) return 0;
    if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
    if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
    return n;
  };

  const isThread = form.post_type === "THREAD";
  const isPoll = form.post_type === "POLL";

  const previewItems = isThread
    ? threadItems.filter((it) => it.text.trim() || it.media.length)
    : [{ text: form.text, media }];

  return (
    <MasterLayout>
      <div className="gmb-premium fb-premium tw-premium">
        {loading && <Loader />}

        {/* ------------------------- Topbar ------------------------- */}
        <div className="gmb-topbar">
          <div className="gmb-topbar-title">
            <h5 className="gmb-display">X (Twitter)</h5>
            <span className="gmb-royal-badge">
              <Icon icon="ri:twitter-x-fill" /> Social Suite
            </span>
            {account && (
              <span className="gmb-live">
                <span className="gmb-live-dot" /> Live
              </span>
            )}
          </div>
          <div className="gmb-topbar-actions">
            <button
              className="btn btn-outline-primary-600"
              onClick={handleConnect}
            >
              <Icon icon="mdi:account-switch-outline" /> Switch Account
            </button>
            <button className="btn btn-primary-600" onClick={openComposer}>
              <Icon icon="ic:baseline-plus" /> Create Post
            </button>
          </div>
        </div>

        {/* ------------------------- Account hero ------------------------- */}
        {account && (
          <div className="fb-page-hero">
            <img
              src={account.profileImage || ""}
              alt={account.username}
              onError={(e) => (e.currentTarget.style.visibility = "hidden")}
            />
            <div>
              <h6>
                {account.name}
                {account.verified && (
                  <Icon
                    icon="mdi:check-decagram"
                    className="tw-verified ms-1"
                  />
                )}
              </h6>
              <span>@{account.username}</span>
            </div>
            <div className="fb-page-stats">
              <div>
                <b className="gmb-mono">{compact(account.followersCount)}</b>
                <small>Followers</small>
              </div>
              <div>
                <b className="gmb-mono">{compact(account.followingCount)}</b>
                <small>Following</small>
              </div>
              <div>
                <b className="gmb-mono">{compact(account.tweetCount)}</b>
                <small>Posts</small>
              </div>
            </div>
          </div>
        )}

        {/* ------------------------- Tabs ------------------------- */}
        <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
          <Nav className="nav-tabs">
            <Nav.Item>
              <Nav.Link eventKey="account">
                <Icon icon="mdi:information-outline" /> Profile
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="tweets">
                <Icon icon="mdi:message-outline" /> Posts
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="insights">
                <Icon icon="mdi:chart-line" /> Analytics
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="mentions">
                <Icon icon="mdi:at" /> Mentions
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="search">
                <Icon icon="mdi:magnify" /> Listening
              </Nav.Link>
            </Nav.Item>
          </Nav>

          <Tab.Content>
            {/* ------------------ PROFILE ------------------ */}
            <Tab.Pane eventKey="account">
              {!account ? (
                <div className="gmb-empty">
                  No X account is connected yet — use “Switch Account” above.
                </div>
              ) : (
                <div className="gmb-card" style={{ padding: 20 }}>
                  <div className="gmb-detail-row">
                    <strong>Name</strong>
                    <span>{account.name}</span>
                  </div>
                  <div className="gmb-detail-row">
                    <strong>Handle</strong>
                    <span>@{account.username}</span>
                  </div>
                  <div className="gmb-detail-row">
                    <strong>Bio</strong>
                    <span>{account.description || "-"}</span>
                  </div>
                  <div className="gmb-detail-row">
                    <strong>Location</strong>
                    <span>{account.location || "-"}</span>
                  </div>
                  <div className="gmb-detail-row">
                    <strong>Website</strong>
                    <span>
                      {account.website ? (
                        <a
                          href={account.website}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {account.website}
                        </a>
                      ) : (
                        "-"
                      )}
                    </span>
                  </div>
                  <div className="gmb-detail-row">
                    <strong>Joined</strong>
                    <span>{fmtDate(account.createdAt)}</span>
                  </div>
                  <div className="gmb-detail-row">
                    <strong>Listed In</strong>
                    <span>{account.listedCount} lists</span>
                  </div>
                  <div className="gmb-detail-row">
                    <strong>Profile</strong>
                    <span>
                      <a
                        href={account.profileUrl}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {account.profileUrl}
                      </a>
                    </span>
                  </div>
                </div>
              )}
            </Tab.Pane>

            {/* ------------------ POSTS ------------------ */}
            <Tab.Pane eventKey="tweets">
              <div className="d-flex justify-content-end mb-3">
                <button
                  className="btn btn-outline-primary-600 btn-sm"
                  onClick={loadTweets}
                >
                  <Icon icon="mdi:refresh" /> Refresh
                </button>
              </div>

              {tweets.length === 0 ? (
                <div className="gmb-empty">No posts published yet.</div>
              ) : (
                <div className="row g-3">
                  {tweets.map((t) => (
                    <div className="col-xl-4 col-lg-6" key={t.tweetId}>
                      <div className="gmb-card tw-tweet-card">
                        <div className="tw-tweet-head">
                          <img
                            className="tw-avatar"
                            src={account?.profileImage || ""}
                            alt=""
                            onError={(e) =>
                              (e.currentTarget.style.visibility = "hidden")
                            }
                          />
                          <div>
                            <b>{account?.name || "Your Account"}</b>
                            <small>
                              @{account?.username} · {fmtDate(t.createdAt)}
                            </small>
                          </div>
                        </div>

                        <div className="tw-tweet-body">{t.text}</div>

                        {t.media?.[0]?.url && (
                          <img
                            className="tw-tweet-media"
                            src={t.media[0].url}
                            alt=""
                          />
                        )}

                        <div className="tw-metric-row">
                          <span>
                            <Icon icon="mdi:heart-outline" />{" "}
                            {compact(t.likeCount)}
                          </span>
                          <span>
                            <Icon icon="mdi:repeat-variant" />{" "}
                            {compact(t.retweetCount)}
                          </span>
                          <span>
                            <Icon icon="mdi:comment-outline" />{" "}
                            {compact(t.replyCount)}
                          </span>
                          <span className="tw-impressions">
                            <Icon icon="mdi:chart-bar" />{" "}
                            {compact(t.impressionCount)}
                          </span>
                        </div>

                        <div className="d-flex gap-2 mt-3">
                          <button
                            className="btn btn-outline-primary-600 btn-sm"
                            onClick={() => openReplies(t)}
                          >
                            <Icon icon="mdi:comment-text-outline" /> Replies
                          </button>

                          <a
                            className="btn btn-outline-primary-600 btn-sm"
                            href={t.permalink}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <Icon icon="mdi:open-in-new" />
                          </a>
                          <button
                            className="btn btn-outline-danger-600 btn-sm"
                            onClick={() => setConfirmDelete(t.tweetId)}
                          >
                            <Icon icon="fluent:delete-24-regular" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Tab.Pane>

            {/* ------------------ ANALYTICS ------------------ */}
            <Tab.Pane eventKey="insights">
              <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                <div className="gmb-tab-sub mb-0">
                  X reports metrics per post rather than by date, so this covers
                  your most recent posts.
                </div>
                <button
                  className="btn btn-outline-primary-600 btn-sm"
                  onClick={loadInsights}
                >
                  <Icon icon="mdi:refresh" /> Refresh
                </button>
              </div>

              {!insights ? (
                <div className="gmb-empty">No analytics loaded yet.</div>
              ) : (
                <>
                  <div className="row g-3 mb-4">
                    {[
                      [
                        "Impressions",
                        insights.totals.impressions,
                        "mdi:chart-bar",
                      ],
                      [
                        "Engagements",
                        insights.totals.engagements,
                        "mdi:gesture-tap",
                      ],
                      ["Likes", insights.totals.likes, "mdi:heart-outline"],
                      [
                        "Reposts",
                        insights.totals.retweets,
                        "mdi:repeat-variant",
                      ],
                      [
                        "Replies",
                        insights.totals.replies,
                        "mdi:comment-outline",
                      ],
                      [
                        "Quotes",
                        insights.totals.quotes,
                        "mdi:format-quote-close",
                      ],
                      [
                        "Engagement Rate",
                        `${insights.totals.engagementRate}%`,
                        "mdi:chart-donut",
                      ],
                      [
                        "Posts Analysed",
                        insights.tweetsAnalysed,
                        "mdi:counter",
                      ],
                    ].map(([label, value, icon]) => (
                      <div className="col-xl-3 col-lg-4 col-md-6" key={label}>
                        <div className="gmb-card gmb-metric-card">
                          <div
                            className="gmb-metric-icon"
                            style={{
                              background: "var(--info-50)",
                              color: "var(--tw-accent)",
                            }}
                          >
                            <Icon icon={icon} />
                          </div>
                          <div className="gmb-metric-label">{label}</div>
                          <div className="gmb-metric-total gmb-mono">
                            {typeof value === "number" ? compact(value) : value}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {insights.topTweets?.length > 0 && (
                    <div className="gmb-card" style={{ padding: 20 }}>
                      <div className="gmb-section-title mb-3">
                        Top Performing Posts
                      </div>
                      {insights.topTweets.map((t, i) => (
                        <div className="tw-top-row" key={t.tweetId}>
                          <span className="tw-top-rank">{i + 1}</span>
                          <div className="tw-top-text">
                            {t.text}
                            {t.permalink && (
                              <>
                                {" "}
                                <a
                                  className="gmb-competitor-maps-link"
                                  href={t.permalink}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  View
                                </a>
                              </>
                            )}
                          </div>
                          <div className="tw-top-stat">
                            <b>{compact(t.engagements)}</b>
                            <small>{t.engagementRate}% rate</small>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </Tab.Pane>

            {/* ------------------ MENTIONS ------------------ */}
            <Tab.Pane eventKey="mentions">
              <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                <div className="gmb-tab-sub mb-0">
                  Everyone who mentioned your account recently.
                </div>
                <button
                  className="btn btn-outline-primary-600 btn-sm"
                  onClick={loadMentions}
                >
                  <Icon icon="mdi:refresh" /> Refresh
                </button>
              </div>

              {mentions.length === 0 ? (
                <div className="gmb-empty">No mentions found.</div>
              ) : (
                <div className="d-flex flex-column gap-3">
                  {mentions.map((m) => (
                    <div className="gmb-card tw-listen-card" key={m.tweetId}>
                      <div className="tw-listen-head">
                        {m.authorImage ? (
                          <img
                            className="tw-avatar"
                            src={m.authorImage}
                            alt=""
                          />
                        ) : (
                          <div className="fb-comment-avatar">
                            {initials(m.authorName)}
                          </div>
                        )}
                        <div>
                          <b>
                            {m.authorName}
                            {m.authorVerified && (
                              <Icon
                                icon="mdi:check-decagram"
                                className="tw-verified ms-1"
                              />
                            )}
                          </b>
                          <div className="tw-listen-handle">
                            @{m.authorUsername} · {fmtDate(m.createdAt)}
                          </div>
                        </div>
                      </div>

                      <div className="tw-tweet-body">{m.text}</div>

                      <div className="d-flex gap-2 flex-wrap">
                        <input
                          className="form-control"
                          placeholder="Write a reply..."
                          style={{ flex: 1, minWidth: 180 }}
                          value={replyBox[m.tweetId] || ""}
                          onChange={(e) =>
                            setReplyBox((s) => ({
                              ...s,
                              [m.tweetId]: e.target.value,
                            }))
                          }
                        />
                        <button
                          className="btn btn-primary-600"
                          onClick={() => sendReply(m.tweetId)}
                        >
                          Reply
                        </button>
                        <button
                          className="btn btn-outline-primary-600"
                          onClick={() => react(m.tweetId, "like")}
                        >
                          <Icon icon="mdi:heart-outline" />
                        </button>
                        <button
                          className="btn btn-outline-primary-600"
                          onClick={() => react(m.tweetId, "retweet")}
                        >
                          <Icon icon="mdi:repeat-variant" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Tab.Pane>

            {/* ------------------ LISTENING ------------------ */}
            <Tab.Pane eventKey="search">
              <div className="tw-search-bar">
                <div style={{ flex: 2 }}>
                  <label>Search Query</label>
                  <input
                    className="form-control"
                    placeholder='Eg: "coffee shop" bhopal'
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && runSearch()}
                  />
                </div>
                <div>
                  <label>Min Likes</label>
                  <input
                    type="number"
                    className="form-control"
                    placeholder="0"
                    value={minLikes}
                    onChange={(e) => setMinLikes(e.target.value)}
                  />
                </div>
                <button className="btn btn-primary-600" onClick={runSearch}>
                  <Icon icon="mdi:magnify" /> Search
                </button>
              </div>

              <div className="gmb-tab-sub">
                Recent search covers the last 7 days. Use operators like
                from:handle, -is:retweet or "exact phrase" to narrow results.
              </div>

              {!searchResults ? (
                <div className="gmb-empty">
                  Search a keyword, brand or competitor to see what people are
                  posting.
                </div>
              ) : searchResults.results.length === 0 ? (
                <div className="gmb-empty">
                  No posts found for “{searchResults.query}”.
                </div>
              ) : (
                <div className="d-flex flex-column gap-3">
                  {searchResults.results.map((r) => (
                    <div className="gmb-card tw-listen-card" key={r.tweetId}>
                      <div className="tw-listen-head">
                        {r.authorImage ? (
                          <img
                            className="tw-avatar"
                            src={r.authorImage}
                            alt=""
                          />
                        ) : (
                          <div className="fb-comment-avatar">
                            {initials(r.authorName)}
                          </div>
                        )}
                        <div>
                          <b>
                            {r.authorName}
                            {r.authorVerified && (
                              <Icon
                                icon="mdi:check-decagram"
                                className="tw-verified ms-1"
                              />
                            )}
                          </b>
                          <div className="tw-listen-handle">
                            @{r.authorUsername} · {fmtDate(r.createdAt)}
                          </div>
                        </div>
                        <span className="tw-listen-followers">
                          {compact(r.authorFollowers)} followers
                        </span>
                      </div>

                      <div className="tw-tweet-body">{r.text}</div>

                      <div className="tw-metric-row">
                        <span>
                          <Icon icon="mdi:heart-outline" />{" "}
                          {compact(r.likeCount)}
                        </span>
                        <span>
                          <Icon icon="mdi:repeat-variant" />{" "}
                          {compact(r.retweetCount)}
                        </span>
                        <span>
                          <Icon icon="mdi:comment-outline" />{" "}
                          {compact(r.replyCount)}
                        </span>

                        <a
                          className="gmb-competitor-maps-link ms-auto"
                          href={r.permalink}
                          target="_blank"
                          rel="noreferrer"
                        >
                          View
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Tab.Pane>
          </Tab.Content>
        </Tab.Container>

        {/* ==================== COMPOSER MODAL ==================== */}
        <Modal
          className="gmb-modal fb-composer-modal"
          size="xl"
          centered
          show={showComposer}
          onHide={() => setShowComposer(false)}
        >
          <Modal.Body>
            <div className="fb-composer">
              {/* ---------- LEFT: social profiles ---------- */}
              <div className="fb-composer-col fb-composer-rail">
                <div className="fb-composer-heading">
                  Select Social Profiles
                </div>

                {SOCIAL_PROFILES.map((sp) => {
                  const connected = sp.key === "twitter" && !!account;
                  return (
                    <div
                      key={sp.key}
                      className={`fb-social-item ${sp.key} ${
                        connected ? "is-connected" : ""
                      }`}
                    >
                      <div className="fb-social-head">
                        <Icon icon={sp.icon} />
                        {sp.label}
                        {connected && (
                          <span className="fb-social-count">(1)</span>
                        )}
                      </div>
                      <div className="fb-social-foot">
                        <button
                          type="button"
                          disabled={!sp.ready}
                          onClick={sp.ready ? handleConnect : undefined}
                        >
                          {connected ? "Connected" : "Connect Account"}
                        </button>
                        <span className="fb-social-status">
                          <Icon
                            icon={
                              connected
                                ? "mdi:check-circle"
                                : "mdi:check-circle-outline"
                            }
                          />
                        </span>
                      </div>
                    </div>
                  );
                })}

                <div className="fb-rail-divider" />

                <div className="fb-field-title">Who Can Reply</div>
                <select
                  className="form-control"
                  value={form.reply_settings}
                  onChange={(e) =>
                    setForm((s) => ({ ...s, reply_settings: e.target.value }))
                  }
                >
                  {REPLY_SETTINGS.map((r) => (
                    <option key={r.key} value={r.key}>
                      {r.label}
                    </option>
                  ))}
                </select>

                <div className="fb-toggle-row mt-4">
                  <span>Scheduling</span>
                  <button type="button" className="fb-switch" disabled />
                </div>
                <div className="gmb-char-count">
                  X has no native scheduling on this API tier — posts publish
                  immediately.
                </div>
              </div>

              {/* ---------- MIDDLE: editor ---------- */}
              <div className="fb-composer-col">
                <div className="fb-composer-title">Create Post</div>
                <div className="fb-composer-sub">
                  {account
                    ? `@${account.username} selected`
                    : "No account selected"}
                </div>

                <div className="fb-channel-tabs">
                  {CHANNEL_TABS.map((c) => (
                    <button
                      key={c.key}
                      type="button"
                      disabled={!c.ready}
                      className={channel === c.key ? "active" : ""}
                      onClick={() => c.ready && setChannel(c.key)}
                    >
                      {c.label}
                    </button>
                  ))}
                </div>

                <div className="fb-account-row">
                  <Icon
                    icon="ri:twitter-x-fill"
                    style={{ color: "var(--tw-brand)" }}
                  />
                  X Account:
                  <span className="fb-account-chip">
                    {account ? `@${account.username}` : "Not connected"}
                  </span>
                </div>

                <div className="fb-field-title">Select Post Type</div>
                <div className="fb-pills">
                  {POST_TYPES.map((t) => (
                    <button
                      key={t.key}
                      type="button"
                      className={`fb-pill ${
                        form.post_type === t.key ? "active" : ""
                      }`}
                      onClick={() =>
                        setForm((s) => ({ ...s, post_type: t.key }))
                      }
                    >
                      {form.post_type === t.key ? (
                        <Icon icon="mdi:check" />
                      ) : (
                        <Icon icon={t.icon} />
                      )}
                      {t.label}
                    </button>
                  ))}
                </div>

                {/* ---- Thread builder ---- */}
                {isThread ? (
                  <>
                    <div className="fb-field-title">
                      Thread ( each tweet posts as a reply to the one above )
                    </div>

                    {threadItems.map((item, i) => (
                      <div className="tw-thread-item" key={i}>
                        <div className="tw-thread-head">
                          <span className="tw-thread-index">
                            Tweet {i + 1} of {threadItems.length}
                          </span>
                          <button
                            type="button"
                            className="tw-thread-remove"
                            disabled={threadItems.length <= 2}
                            onClick={() =>
                              setThreadItems((s) =>
                                s.filter((_, idx) => idx !== i),
                              )
                            }
                          >
                            <Icon icon="mdi:close" />
                          </button>
                        </div>

                        <textarea
                          className="form-control"
                          rows={3}
                          placeholder={
                            i === 0
                              ? "Start with your strongest point..."
                              : "Continue the thread..."
                          }
                          value={item.text}
                          onChange={(e) =>
                            setThreadItems((s) =>
                              s.map((it, idx) =>
                                idx === i
                                  ? { ...it, text: e.target.value }
                                  : it,
                              ),
                            )
                          }
                        />

                        <CharRing count={countChars(item.text)} />

                        <div className="tw-media-strip">
                          {item.media.map((m, mi) => (
                            <div className="tw-media-item" key={m.mediaId}>
                              {m.kind === "VIDEO" ? (
                                <video src={m.preview} muted />
                              ) : (
                                <img src={m.preview} alt="" />
                              )}
                              <button
                                type="button"
                                onClick={() =>
                                  setThreadItems((s) =>
                                    s.map((it, idx) =>
                                      idx === i
                                        ? {
                                            ...it,
                                            media: it.media.filter(
                                              (_, x) => x !== mi,
                                            ),
                                          }
                                        : it,
                                    ),
                                  )
                                }
                              >
                                <Icon icon="mdi:close" />
                              </button>
                            </div>
                          ))}
                          {item.media.length < 4 && (
                            <label className="tw-media-add">
                              <input
                                type="file"
                                accept="image/*,video/*"
                                onChange={(e) =>
                                  onPickThreadMedia(i, e.target.files[0])
                                }
                              />
                              <Icon icon="mdi:plus" />
                            </label>
                          )}
                        </div>
                      </div>
                    ))}

                    <button
                      type="button"
                      className="tw-thread-add"
                      disabled={threadItems.length >= 25}
                      onClick={() =>
                        setThreadItems((s) => [...s, { text: "", media: [] }])
                      }
                    >
                      <Icon icon="mdi:plus" /> Add Tweet
                    </button>
                  </>
                ) : (
                  <>
                    <div className="fb-field-title">Write a Post</div>
                    <textarea
                      className="form-control"
                      rows={5}
                      placeholder="Eg: We just shipped something our customers kept asking for..."
                      value={form.text}
                      onChange={(e) =>
                        setForm((s) => ({ ...s, text: e.target.value }))
                      }
                    />
                    <CharRing count={mainCount} />

                    {/* ---- Poll ---- */}
                    {isPoll ? (
                      <>
                        <div className="fb-field-title mt-3">
                          Poll Options ( 2 - 4 )
                        </div>
                        {pollOptions.map((opt, i) => (
                          <div className="li-poll-option" key={i}>
                            <input
                              className="form-control"
                              placeholder={`Option ${i + 1}`}
                              maxLength={25}
                              value={opt}
                              onChange={(e) =>
                                setPollOptions((s) =>
                                  s.map((o, idx) =>
                                    idx === i ? e.target.value : o,
                                  ),
                                )
                              }
                            />
                            <button
                              type="button"
                              disabled={pollOptions.length <= 2}
                              onClick={() =>
                                setPollOptions((s) =>
                                  s.filter((_, idx) => idx !== i),
                                )
                              }
                            >
                              <Icon icon="mdi:close" />
                            </button>
                          </div>
                        ))}
                        <button
                          type="button"
                          className="li-poll-add"
                          disabled={pollOptions.length >= 4}
                          onClick={() => setPollOptions((s) => [...s, ""])}
                        >
                          <Icon icon="mdi:plus" /> Add Option
                        </button>

                        <div className="fb-field-title mt-3">Poll Duration</div>
                        <select
                          className="form-control"
                          value={form.poll_duration}
                          onChange={(e) =>
                            setForm((s) => ({
                              ...s,
                              poll_duration: Number(e.target.value),
                            }))
                          }
                        >
                          {POLL_DURATIONS.map((d) => (
                            <option key={d.key} value={d.key}>
                              {d.label}
                            </option>
                          ))}
                        </select>
                        <div className="gmb-char-count">
                          A poll cannot be combined with images or video.
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="fb-field-title mt-3">
                          Add Media ( up to 4 images, or 1 video )
                        </div>
                        <div className="tw-media-strip">
                          {media.map((m, i) => (
                            <div className="tw-media-item" key={m.mediaId}>
                              {m.kind === "VIDEO" ? (
                                <video src={m.preview} muted />
                              ) : (
                                <img src={m.preview} alt="" />
                              )}
                              <button
                                type="button"
                                onClick={() =>
                                  setMedia((s) => s.filter((_, x) => x !== i))
                                }
                              >
                                <Icon icon="mdi:close" />
                              </button>
                            </div>
                          ))}
                          {media.length < 4 && (
                            <label className="tw-media-add">
                              <input
                                type="file"
                                accept="image/*,video/*"
                                onChange={(e) => onPickMedia(e.target.files[0])}
                              />
                              <Icon icon="mdi:plus" />
                            </label>
                          )}
                        </div>
                        <div className="gmb-char-count mt-2">
                          Images up to 5 MB, GIFs up to 15 MB, video up to 512
                          MB.
                        </div>
                      </>
                    )}
                  </>
                )}

                <div className="fb-submit-bar">
                  <button
                    type="button"
                    className="btn btn-outline-primary-600"
                    onClick={() => setShowComposer(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="fb-submit"
                    disabled={!account}
                    onClick={publish}
                  >
                    <Icon icon="ri:twitter-x-fill" />
                    {isThread ? "Publish Thread" : "Publish To X"}
                  </button>
                </div>
              </div>

              {/* ---------- RIGHT: preview + tips ---------- */}
              <div className="fb-composer-col fb-composer-aside">
                <div className="fb-preview">
                  <div className="fb-preview-head">
                    <Icon
                      icon="ri:twitter-x-fill"
                      style={{ color: "var(--tw-brand)" }}
                    />
                    X Preview
                  </div>

                  {previewItems.length === 0 ? (
                    <div className="fb-preview-empty">
                      <Icon
                        icon="mdi:message-outline"
                        style={{ fontSize: 34 }}
                      />
                      Nothing to preview yet
                    </div>
                  ) : (
                    previewItems.map((item, i) => (
                      <div className="tw-preview-tweet" key={i}>
                        <div className="tw-preview-line">
                          <img
                            className="tw-avatar"
                            src={account?.profileImage || ""}
                            alt=""
                            onError={(e) =>
                              (e.currentTarget.style.visibility = "hidden")
                            }
                          />
                        </div>
                        <div className="tw-preview-content">
                          <b>{account?.name || "Your Account"}</b>
                          <span>@{account?.username || "handle"}</span>
                          <p>{item.text || "Your content here"}</p>

                          {item.media?.[0] && (
                            <img src={item.media[0].preview} alt="" />
                          )}

                          {isPoll && i === 0 && (
                            <div className="tw-preview-poll">
                              {pollOptions
                                .filter((o) => o.trim())
                                .map((o, oi) => (
                                  <div key={oi}>{o}</div>
                                ))}
                            </div>
                          )}

                          <div className="tw-preview-actions">
                            <Icon icon="mdi:comment-outline" />
                            <Icon icon="mdi:repeat-variant" />
                            <Icon icon="mdi:heart-outline" />
                            <Icon icon="mdi:chart-bar" />
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>

                <div className="fb-composer-heading">Tips to Improve Reach</div>
                {TIPS.map((t) => (
                  <div className="fb-tip-card" key={t.title}>
                    <div className="fb-tip-icon">
                      <Icon icon="mdi:lightbulb-on-outline" />
                    </div>
                    <h6>{t.title}</h6>
                    <p>{t.body}</p>
                  </div>
                ))}
              </div>
            </div>
          </Modal.Body>
        </Modal>

        {/* ==================== REPLIES MODAL ==================== */}
        <Modal
          className="gmb-modal"
          size="lg"
          centered
          show={showReplies}
          onHide={() => setShowReplies(false)}
        >
          <Modal.Header closeButton className="border-0 pb-0">
            <Modal.Title>Replies</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {replyTweet && (
              <div className="gmb-card tw-tweet-card mb-3">
                <div className="tw-tweet-body">{replyTweet.text}</div>
                <div className="tw-metric-row">
                  <span>
                    <Icon icon="mdi:heart-outline" />{" "}
                    {compact(replyTweet.likeCount)}
                  </span>
                  <span>
                    <Icon icon="mdi:repeat-variant" />{" "}
                    {compact(replyTweet.retweetCount)}
                  </span>
                  <span className="tw-impressions">
                    <Icon icon="mdi:chart-bar" />{" "}
                    {compact(replyTweet.impressionCount)}
                  </span>
                </div>
              </div>
            )}

            <div className="d-flex gap-2 mb-3">
              <input
                className="form-control"
                placeholder="Write a reply..."
                value={replyBox[replyTweet?.tweetId] || ""}
                onChange={(e) =>
                  setReplyBox((s) => ({
                    ...s,
                    [replyTweet.tweetId]: e.target.value,
                  }))
                }
              />
              <button
                className="btn btn-primary-600"
                onClick={() => sendReply(replyTweet.tweetId)}
              >
                Reply
              </button>
            </div>

            {replies.length === 0 ? (
              <div className="gmb-empty">No replies yet.</div>
            ) : (
              replies.map((r) => (
                <div className="fb-comment" key={r.tweetId}>
                  {r.authorImage ? (
                    <img
                      className="tw-avatar"
                      src={r.authorImage}
                      alt=""
                      style={{ width: 34, height: 34 }}
                    />
                  ) : (
                    <div className="fb-comment-avatar">
                      {initials(r.authorName)}
                    </div>
                  )}
                  <div className="fb-comment-body">
                    <b>
                      {r.authorName}{" "}
                      <span
                        style={{ fontWeight: 400, color: "var(--gmb-ink-3)" }}
                      >
                        @{r.authorUsername}
                      </span>
                    </b>
                    <p>{r.text}</p>
                    <div className="fb-comment-actions">
                      <button
                        onClick={() =>
                          setReplyBox((s) => ({
                            ...s,
                            [r.tweetId]: s[r.tweetId] || "",
                          }))
                        }
                      >
                        Reply
                      </button>
                      <button onClick={() => react(r.tweetId, "like")}>
                        Like
                      </button>
                      {r.isOwn && (
                        <button
                          className="danger"
                          onClick={() => setConfirmDelete(r.tweetId)}
                        >
                          Delete
                        </button>
                      )}
                      <span style={{ color: "var(--gmb-ink-3)" }}>
                        {fmtDate(r.createdAt)}
                      </span>
                    </div>
                    {replyBox[r.tweetId] !== undefined && (
                      <div className="d-flex gap-2 mt-2">
                        <input
                          className="form-control"
                          placeholder="Write a reply..."
                          value={replyBox[r.tweetId]}
                          onChange={(e) =>
                            setReplyBox((s) => ({
                              ...s,
                              [r.tweetId]: e.target.value,
                            }))
                          }
                        />
                        <button
                          className="btn btn-primary-600 btn-sm"
                          onClick={() => sendReply(r.tweetId)}
                        >
                          Send
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </Modal.Body>
        </Modal>

        {/* ==================== DELETE CONFIRM ==================== */}
        <Modal
          className="gmb-modal"
          size="sm"
          centered
          show={!!confirmDelete}
          onHide={() => setConfirmDelete(null)}
        >
          <Modal.Body className="text-center">
            <Icon
              icon="fluent:delete-24-regular"
              style={{ fontSize: 34, color: "var(--danger-main)" }}
            />
            <p className="mt-2 mb-0">
              Please confirm, do you want to delete this post?
            </p>
          </Modal.Body>
          <Modal.Footer>
            <button
              className="btn btn-outline-primary-600"
              onClick={() => setConfirmDelete(null)}
            >
              Cancel
            </button>
            <button className="btn btn-danger-600" onClick={removeTweet}>
              Confirm
            </button>
          </Modal.Footer>
        </Modal>
      </div>
    </MasterLayout>
  );
}

export default TwitterPanel;
